import { _ as __decorate, a as __metadata, F as FormControl, b as FormControlController } from '../index-TB9Y-DiB.js';
import { css, html } from 'lit';
import { property, queryAssignedElements, customElement } from 'lit/decorators.js';
import 'lit/directive.js';

/**
 * Roving Tabindex Controller
 *
 * Implements the ARIA roving tabindex pattern for keyboard navigation.
 * See: https://www.w3.org/WAI/ARIA/apg/practices/keyboard-interface/#kbd_roving_tabindex
 *
 * Usage:
 * ```ts
 * private rovingTabindex = new RovingTabindexController(this, {
 *   getItems: () => Array.from(this.querySelectorAll('my-item')),
 *   onSelect: (item) => this.selectItem(item),
 * });
 * ```
 */
class RovingTabindexController {
    constructor(host, config) {
        this.host = host;
        this.config = {
            wrap: true,
            direction: 'both',
            selectOnFocus: false,
            selectOnHomeEnd: false,
            skipItem: (item) => item.hasAttribute('disabled') || item.disabled === true,
            ...config,
        };
        host.addController(this);
    }
    // eslint-disable-next-line class-methods-use-this
    hostConnected() {
        // Controller is ready when host connects
    }
    // eslint-disable-next-line class-methods-use-this
    hostDisconnected() {
        // Cleanup if needed
    }
    /**
     * Update tabindex values for all items
     * @param activeIndex - Index of the item that should be tabbable (default: 0 or first non-disabled)
     */
    updateTabindices(activeIndex) {
        const items = this.config.getItems();
        if (!items || items.length === 0) {
            return;
        }
        // Find the active index
        let targetIndex = activeIndex ?? 0;
        // If no active index specified, use first non-disabled item
        if (activeIndex === undefined) {
            targetIndex = items.findIndex((item) => !this.config.skipItem(item));
            if (targetIndex === -1) {
                targetIndex = 0; // Fallback to first item if all disabled
            }
        }
        // Set tabindex for all items
        items.forEach((item, index) => {
            const itemElement = item;
            if (this.config.skipItem(itemElement)) {
                // eslint-disable-next-line no-param-reassign
                itemElement.tabIndex = -1;
            }
            else {
                // eslint-disable-next-line no-param-reassign
                itemElement.tabIndex = index === targetIndex ? 0 : -1;
            }
        });
    }
    /**
     * Handle keyboard navigation
     * @param currentItem - The currently focused item
     * @param event - The keyboard event
     * @returns true if the event was handled, false otherwise
     */
    handleKeydown(currentItem, event) {
        const direction = typeof this.config.direction === 'function' ? this.config.direction() : this.config.direction;
        const { key } = event;
        // Determine if this key should be handled based on direction
        const isVertical = key === 'ArrowUp' || key === 'ArrowDown';
        const isHorizontal = key === 'ArrowLeft' || key === 'ArrowRight';
        const isHome = key === 'Home';
        const isEnd = key === 'End';
        const shouldHandle = isHome ||
            isEnd ||
            (direction === 'both' && (isVertical || isHorizontal)) ||
            (direction === 'vertical' && isVertical) ||
            (direction === 'horizontal' && isHorizontal);
        if (!shouldHandle) {
            return false;
        }
        // Prevent default behavior (page scrolling)
        event.preventDefault();
        const items = this.config.getItems();
        const currentIndex = items.indexOf(currentItem);
        if (currentIndex === -1) {
            return false;
        }
        let nextIndex = currentIndex;
        // Handle Home/End keys
        if (isHome) {
            nextIndex = 0;
        }
        else if (isEnd) {
            nextIndex = items.length - 1;
        }
        else {
            // Handle arrow keys
            const isNext = key === 'ArrowDown' || key === 'ArrowRight';
            const isPrev = key === 'ArrowUp' || key === 'ArrowLeft';
            if (isNext) {
                nextIndex = this.getNextIndex(items, currentIndex, 1);
            }
            else if (isPrev) {
                nextIndex = this.getNextIndex(items, currentIndex, -1);
            }
        }
        // Skip disabled items — Home always searches forward, End always searches backward
        // eslint-disable-next-line no-nested-ternary
        const searchDirection = isHome ? 1 : isEnd ? -1 : nextIndex > currentIndex ? 1 : -1;
        nextIndex = this.findNextValidIndex(items, nextIndex, searchDirection);
        if (nextIndex !== -1 && nextIndex !== currentIndex) {
            const nextItem = items[nextIndex];
            // Update tabindices
            this.updateTabindices(nextIndex);
            // Focus the next item
            nextItem.focus();
            // Optionally select/activate the item.
            // Select when `selectOnFocus` is enabled, or when Home/End keys
            // were used and the consumer explicitly opted in via `selectOnHomeEnd`.
            const shouldSelect = this.config.selectOnFocus === true || ((isHome || isEnd) && this.config.selectOnHomeEnd === true);
            if (shouldSelect && this.config.onSelect) {
                this.config.onSelect(nextItem, event);
            }
            return true;
        }
        return false;
    }
    /**
     * Get the next index based on direction
     */
    getNextIndex(items, currentIndex, direction) {
        const { wrap } = this.config;
        let nextIndex = currentIndex + direction;
        if (wrap) {
            // Wrap around
            if (nextIndex < 0) {
                nextIndex = items.length - 1;
            }
            else if (nextIndex >= items.length) {
                nextIndex = 0;
            }
        }
        else {
            // Clamp to bounds
            nextIndex = Math.max(0, Math.min(items.length - 1, nextIndex));
        }
        return nextIndex;
    }
    /**
     * Find the next valid (non-disabled) index
     */
    findNextValidIndex(items, startIndex, direction) {
        const maxAttempts = items.length;
        let attempts = 0;
        let index = startIndex;
        while (attempts < maxAttempts) {
            if (!this.config.skipItem(items[index])) {
                return index;
            }
            index = this.getNextIndex(items, index, direction);
            attempts += 1;
        }
        // All items are disabled
        return -1;
    }
    /**
     * Set focus to a specific item
     */
    focusItem(item) {
        const items = this.config.getItems();
        const index = items.indexOf(item);
        if (index !== -1) {
            this.updateTabindices(index);
            item.focus();
        }
    }
    /**
     * Set focus to the first non-disabled item
     */
    focusFirst() {
        const items = this.config.getItems();
        const firstValidIndex = this.findNextValidIndex(items, 0, 1);
        if (firstValidIndex !== -1) {
            this.focusItem(items[firstValidIndex]);
        }
    }
    /**
     * Set focus to the last non-disabled item
     */
    focusLast() {
        const items = this.config.getItems();
        const lastValidIndex = this.findNextValidIndex(items, items.length - 1, -1);
        if (lastValidIndex !== -1) {
            this.focusItem(items[lastValidIndex]);
        }
    }
}

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

:root {
  --bsi-form-control-height: 2.5rem;
  --bsi-form-control-spacing: var(--bsi-spacing-xxs);
  --bsi-form-control-background-color: var(--bsi-color-background-inverse);
  --bsi-form-control-border-color: var(--bsi-color-border-secondary);
  --bsi-form-control-border-radius: var(--bsi-radius-smooth);
  --bsi-form-control-placeholder-color: var(--bsi-color-text-muted);
  --bsi-form-control-label-color: var(--bsi-color-text-base);
  --bsi-form-control-text-color: var(--bsi-color-text-secondary);
  --bsi-form-control-font-size: var(--bsi-body-font-size);
  --bsi-form-group-spacing-y: var(--bsi-spacing-m);
  --bsi-form-checkbox-border-color: var(--bsi-color-border-secondary);
  --bsi-form-checkbox-border-radius: var(--bsi-radius-smooth);
  --bsi-form-checked-color: var(--bsi-color-background-primary);
}

input[readonly],
textarea[readonly],
select[readonly] {
  --bsi-form-control-border-color: var(--bsi-color-border-subtle);
  --bsi-form-control-background-color: var(--bsi-color-background-muted);
  cursor: not-allowed;
}

input,
textarea,
select {
  display: block;
  width: 100%;
  padding: var(--bsi-form-control-spacing);
  border: 1px solid var(--bsi-form-control-border-color);
  border-radius: var(--bsi-form-control-border-radius);
  background-color: var(--bsi-form-control-background-color);
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-form-control-font-size);
}
input.disabled, input:disabled,
textarea.disabled,
textarea:disabled,
select.disabled,
select:disabled {
  border-color: var(--bsi-color-border-disabled);
  opacity: 1;
  background-color: var(--bsi-color-background-disabled);
  color: var(--bsi-color-text-disabled);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input:focus,
textarea:focus {
  outline: 3px solid transparent;
  outline-offset: 3px;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
}

input::file-selector-button {
  margin: -0.375rem -0.75rem;
  padding: 0.375rem 0.75rem;
  border-width: 0;
  border-style: solid;
  border-radius: 0;
  border-color: inherit;
  color: hsl(0, 0%, 10%);
  pointer-events: none;
  margin-inline-end: 0.75rem;
  border-inline-end-width: 0;
  background-color: hsl(0, 0%, 100%);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  input::file-selector-button {
    transition: none;
  }
}
input:hover:not(:disabled):not([readonly])::file-selector-button {
  background-color: rgb(242.25, 242.25, 242.25);
}
input[type=file] {
  overflow: hidden;
}
input[type=file]:not(:disabled):not([readonly]) {
  cursor: pointer;
}
input::-webkit-date-and-time-value {
  height: 1.5em;
}

select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='hsl%280, 0%, 32%%29' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right var(--bsi-form-control-spacing) center;
  background-size: 16px 12px;
  appearance: none;
}
select:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
select[multiple], select[size]:not([size="1"]) {
  padding-right: 0.75rem;
}
select:disabled {
  background-color: hsl(0, 0%, 90%);
}
select:disabled:hover {
  cursor: not-allowed;
}
select:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 hsl(0, 0%, 10%);
}
select option {
  font-weight: normal;
}
select {
  /* stylelint-disable-next-line no-duplicate-selectors */ /* TO DO check these styles */
}
select:disabled {
  opacity: 1;
  background-color: hsl(210, 3%, 85%);
}

textarea {
  height: auto;
  font-size: var(--bsi-body-font-size);
  line-height: 1.5;
}

label {
  display: inline-block;
  width: auto;
  max-width: 100%;
  margin-bottom: var(--bsi-spacing-xxs);
  color: var(--bsi-form-control-label-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-label-leading);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input,
textarea {
  outline: 0;
  box-shadow: none;
  transition: none;
  appearance: none;
}

input[type=date],
input[type=datetime-local],
input[type=time] {
  display: flex;
}

fieldset legend {
  margin-bottom: var(--bsi-spacing-s);
  padding: 0 var(--bsi-form-input-spacing-x);
  background-color: transparent;
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
}

::placeholder {
  color: var(--bsi-form-control-placeholder-color);
}

input::-webkit-datetime-edit {
  background-color: var(--bsi-color-background-primary-lighter);
  color: var(--bsi-form-contro-text-color);
}

.form-group {
  position: relative;
  margin-bottom: var(--bsi-form-group-spacing-y);
}
.form-group label.input-symbol-label:not(.active) {
  left: 2.25rem;
}
.form-group small.form-text {
  margin: 0;
  font-size: 0.875rem;
}
.form-group input[type=time] ~ label {
  font-size: 0.875rem;
}

.form-text {
  margin: var(--bsi-spacing-xxs) 0;
  color: var(--bsi-color-text-secondary);
}

.form-group.active .form-file-name {
  padding-bottom: 1.95rem;
}

.warning-feedback {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  color: var(--bsi-color-text-warning);
  font-size: 0.75rem;
}

.valid-feedback,
.invalid-feedback,
.warning-feedback {
  margin-left: 0.5rem;
}

.input-group .input-group-text .btn {
  border-radius: var(--bsi-form-control-border-radius) 0 0 var(--bsi-form-control-border-radius);
}
.input-group .input-group-append {
  margin-left: 0;
}
.input-group .input-group-append .btn {
  height: 100%;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom: 1px solid hsl(210, 17%, 44%);
  border-radius: 0 var(--bsi-form-control-border-radius) var(--bsi-form-control-border-radius) 0;
}

.form-check {
  position: relative;
}
.form-check + .form-check {
  margin-top: var(--bsi-spacing-s);
}
.form-check [type=checkbox],
.form-check [type=radio] {
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
  margin-top: 0;
  margin-left: 0;
  opacity: 0;
}
.form-check [type=checkbox] + label,
.form-check [type=radio] + label {
  position: relative;
  margin-bottom: 0;
  padding-left: 28px;
  font-size: var(--bsi-label-font-size);
  font-weight: var(--bsi-font-weight-solid);
  cursor: pointer;
  user-select: none;
}
@media (min-width: 576px) {
  .form-check [type=checkbox] + label,
  .form-check [type=radio] + label {
    font-size: var(--bsi-label-font-size-l);
  }
}
.form-check [type=checkbox]:focus + label,
.form-check [type=radio]:focus + label {
  border-color: hsl(0, 0%, 0%) !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}
.form-check [type=checkbox]:focus[data-focus-mouse=true] + label,
.form-check [type=radio]:focus[data-focus-mouse=true] + label {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}
.form-check input[type=checkbox] + label::after,
.form-check input[type=checkbox] + label::before {
  position: absolute;
  left: 0;
  z-index: 1;
  content: "";
  border-width: 2px;
  border-style: solid;
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=checkbox] + label::after {
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--bsi-form-control-border-radius);
}
.form-check input[type=checkbox]:checked + label::before {
  top: 3px;
  left: 3px;
  width: 6px;
  height: 12px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent var(--bsi-color-border-inverse) var(--bsi-color-border-inverse) transparent;
  opacity: 1;
  transform: rotate(40deg);
  transform-origin: 100% 100%;
  backface-visibility: hidden;
}
.form-check input[type=checkbox]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}
.form-check input[type=checkbox]:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checkbox-border-color);
  background-color: transparent;
}
.form-check input[type=checkbox]:not(:checked) + label::before {
  top: 10px;
  left: 6px;
  width: 0;
  height: 0;
  border-color: transparent;
}
.form-check input[type=checkbox] + label:hover::after {
  border-color: var(--bsi-color-border-secondary-hover);
}
.form-check input[type=checkbox]:disabled + label {
  opacity: 1;
  cursor: not-allowed;
}
.form-check input[type=checkbox]:disabled:not(:checked) + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: transparent;
}
.form-check input[type=checkbox]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio] + label::after, .form-check input[type=radio] + label::before {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  content: "";
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-radius: var(--bsi-radius-rounded);
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=radio]:not(:checked) + label::after, .form-check input[type=radio]:not(:checked) + label::before {
  border-color: var(--bsi-form-checkbox-border-color);
}
.form-check input[type=radio]:not(:checked) + label:after {
  z-index: -1;
  transform: scale(0);
}
.form-check input[type=radio]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
  transform: scale(0.64);
}
.form-check input[type=radio]:checked + label::before {
  border-color: var(--bsi-form-checked-color);
}
.form-check input[type=radio] + label:hover::before {
  border-color: var(--bsi-color-border-secondary-hover);
}
.form-check input[type=radio]:disabled + label {
  cursor: not-allowed;
}
.form-check input[type=radio]:disabled:not(:checked) + label::after, .form-check input[type=radio]:disabled:not(:checked) + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check .form-text {
  display: block;
  margin-bottom: 0.5rem;
  padding-right: 3.25rem;
}
.form-check.form-check-group {
  margin-bottom: 1rem;
  padding: 0 0 1rem 0;
  box-shadow: inset 0 -1px 0 0 rgba(1, 1, 1, 0.1);
}
.form-check.form-check-group input[type=checkbox] + label,
.form-check.form-check-group input[type=radio] + label {
  position: static;
  padding-right: 3.25rem;
  padding-left: 0;
}
.form-check.form-check-group input[type=checkbox] + label::after, .form-check.form-check-group input[type=checkbox] + label::before,
.form-check.form-check-group input[type=radio] + label::after,
.form-check.form-check-group input[type=radio] + label::before {
  right: 0;
  left: auto;
}
.form-check.form-check-group input[type=checkbox]:checked + label::before {
  right: 11px;
}
.form-check.form-check-group input[type=radio]:checked + label::before {
  right: 0;
}
.form-check.form-check-group input.semi-checked:not(:checked) + label::before {
  right: 4px;
  left: auto;
}
.form-check input.semi-checked:not(:checked) + label::before {
  top: 12px;
  left: 4px;
  width: 12px;
  height: 2px;
  border-width: 0;
  border-style: none;
  border-color: transparent;
  opacity: 1;
  background: var(--bsi-color-background-inverse);
  transform: none;
  backface-visibility: hidden;
}
.form-check input.semi-checked:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}

.form-check-inline {
  display: inline-block;
}
.form-check-inline:not(:last-child) {
  margin-right: var(--bsi-spacing-m);
}

@media (prefers-reduced-motion: reduce) {
  fieldset legend,
  .form-group label,
  textarea,
  .form-check [type=checkbox],
  .form-check [type=radio],
  .form-check [type=checkbox] + label::after,
  .form-check [type=checkbox] + label::before,
  .form-check [type=radio] + label::after,
  .form-check [type=radio] + label::before,
  .toggles label input[type=checkbox] + .lever::before,
  .toggles label input[type=checkbox] + .lever::after {
    transition: none !important;
  }
}
.form-control-plaintext {
  border: 0;
  --bsi-form-control-border-color: transparent;
  --bsi-form-control-border-radius: 0;
  --bsi-form-control-background-color: transparent;
  --bsi-form-control-spacing: 0;
}
.form-control-plaintext:focus {
  outline: 0;
  box-shadow: none !important;
}
.form-control-plaintext + label {
  cursor: text;
}

.form-control {
  background-repeat: no-repeat;
  background-position: center right;
  background-size: 45px 20px;
}
.form-control:disabled {
  cursor: not-allowed;
  background: var(--bsi-color-background-disabled);
  border: 0;
  color: var(--bsi-color-text-disabled);
}

.form-control-sm {
  --bsi-form-control-spacing: var(--bsi-spacing-xxs) var(--bsi-spacing-3xs);
  --bsi-form-control-font-size: var(--bsi-label-font-size);
}
.form-control-sm::file-selector-button {
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  margin-inline-end: 0.5rem;
}

.form-control-lg {
  --bsi-form-control-font-size: var(--bsi-lead-font-size);
}
.form-control-lg::file-selector-button {
  padding: 0.5rem 1rem;
  margin: -0.5rem -1rem;
  margin-inline-end: 1rem;
}

textarea.form-control {
  min-height: 2.5rem;
  border: 1px solid hsl(210, 17%, 44%);
}
textarea.form-control-sm {
  min-height: calc(1.5em + 0.5rem);
}
textarea.form-control-lg {
  min-height: calc(1.5em + 1rem);
}

.form-control-color {
  width: 3rem;
  height: 2.5rem;
  padding: 0.375rem;
}
.form-control-color:not(:disabled):not([readonly]) {
  cursor: pointer;
}
.form-control-color::-moz-color-swatch {
  border: 0 !important;
  border-radius: 0;
}
.form-control-color::-webkit-color-swatch {
  border-radius: 0;
}
.form-control-color.form-control-sm {
  height: calc(1.5em + 0.5rem);
}
.form-control-color.form-control-lg {
  height: calc(1.5em + 1rem);
}

.form-check-reverse {
  padding-right: 1.5em;
  padding-left: 0;
  text-align: right;
}
.form-check-reverse .form-check-input {
  float: right;
  margin-right: -1.5em;
  margin-left: 0;
}

.form-check-input {
  width: 1em;
  height: 1em;
  margin-top: 0.25em;
  vertical-align: top;
  background-color: hsl(0, 0%, 100%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 1px solid rgba(0, 0, 0, 0.25);
  appearance: none;
  print-color-adjust: exact;
}
.form-check-input[type=checkbox] {
  border-radius: 0.25em;
}
.form-check-input[type=radio] {
  border-radius: 50%;
}
.form-check-input:active {
  filter: brightness(90%);
}
.form-check-input:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
.form-check-input:checked {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
}
.form-check-input:checked[type=checkbox] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/></svg>"));
}
.form-check-input:checked[type=radio] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='2' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-check-input[type=checkbox]:indeterminate {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/></svg>"));
}
.form-check-input:disabled {
  pointer-events: none;
  filter: none;
  opacity: 0.5;
}
.form-check-input[disabled] ~ .form-check-label, .form-check-input:disabled ~ .form-check-label {
  cursor: default;
  opacity: 0.5;
}

.form-switch {
  padding-left: 2.5em;
}
.form-switch .form-check-input {
  width: 2em;
  margin-left: -2.5em;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='rgba(0, 0, 0, 0.25)'/></svg>"));
  background-position: left center;
  border-radius: 2em;
  transition: background-position 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  .form-switch .form-check-input {
    transition: none;
  }
}
.form-switch .form-check-input:focus {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(210, 17%, 44%)'/></svg>"));
}
.form-switch .form-check-input:checked {
  background-position: right center;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-switch.form-check-reverse {
  padding-right: 2.5em;
  padding-left: 0;
}
.form-switch.form-check-reverse .form-check-input {
  margin-right: -2.5em;
  margin-left: 0;
}

.form-check-inline {
  display: inline-block;
  margin-right: 1rem;
}

.btn-check {
  position: absolute;
  clip: rect(0, 0, 0, 0);
  pointer-events: none;
}
.btn-check[disabled] + .btn, .btn-check:disabled + .btn {
  pointer-events: none;
  filter: none;
  opacity: 0.65;
}

.form-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

.input-group-text:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) ~ .input-group-text,
button:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) + button {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

.input-group-text:has(~ .is-invalid),
.is-invalid ~ .input-group-text,
button:has(~ .is-invalid),
.is-invalid + button {
  border-color: var(--bsi-color-border-danger) !important;
}

.sr-only-justvalidate-bi {
  display: none;
}

.is-valid,
.is-invalid {
  padding-right: calc(1.5em + 0.75rem) !important;
}
.is-valid[type=number],
.is-invalid[type=number] {
  padding-right: calc(1.5em + 0.75rem + var(--bsi-form-control-spacing) + 1rem) !important;
}

.was-validated &:valid,
.is-valid {
  border-color: var(--bsi-color-border-success) !important;
  background-image: url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="%23008055" xmlns="http://www.w3.org/2000/svg"><path d="M9.6,16.9L4,11.4l0.8-0.7l4.8,4.8l8.5-8.4l0.7,0.7L9.6,16.9z" /></svg>');
}
.was-validated &:valid ~ .warning-feedback,
.is-valid ~ .warning-feedback {
  display: block;
}

.was-validated &:invalid,
.is-invalid {
  border-color: var(--bsi-color-border-danger) !important;
  background-image: url('data:image/svg+xml,<svg width="24" fill="%23cc334d" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.7,12l6.7,6.6l-0.8,0.8L12,12.7l-6.6,6.7l-0.8-0.8l6.7-6.6L4.6,5.4l0.8-0.8l6.6,6.7l6.6-6.7l0.8,0.8L12.7,12z" /></svg>');
}

.form-control.warning {
  border-color: var(--bsi-color-border-warning) !important;
  background-image: url('data:image/svg+xml,<svg width="24" height="24" fill="%23995c00" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.5,17h-1V2h1V17z M12.5,20h-1v2h1V20z" /></svg>');
}

.input-group-text:has(~ .is-valid),
.is-valid ~ .input-group-text,
button:has(~ .is-valid),
.is-valid + button {
  border-color: var(--bsi-color-border-success);
}

.is-valid + .input-group-text.align-buttons,
.is-invalid + .input-group-text.align-buttons {
  right: 30px;
}

.is-invalid + .input-group-text.align-buttons {
  bottom: 22px;
}

.autocomplete__wrapper .form-feedback {
  position: absolute;
}

textarea.form-control {
  background-position: top 0.5em right !important;
}

::slotted([slot=label]) {
  display: block;
  padding: 0 var(--bsi-form-input-spacing-x);
  margin-bottom: var(--bsi-spacing-s);
  background-color: rgba(0, 0, 0, 0);
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
}

.it-radio-group {
  margin-bottom: var(--bsi-spacing-xs);
}

.it-radio-group-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bsi-spacing-s);
}`;

/**
 * Radio group component - manages a collection of radio buttons
 *
 * @element it-radio-group
 * @slot - Contains the `<it-radio>` elements
 *
 */
let ItRadioGroup = class ItRadioGroup extends FormControl {
    constructor() {
        super(...arguments);
        /**
         * The name of the radio group, used for form submission
         */
        this.name = '';
        /**
         * The currently selected value
         */
        this.value = '';
        /**
         * Whether the radio group is disabled
         */
        this.disabled = false;
        /**
         * Whether a selection is required
         */
        this.required = false;
        /**
         * Optional prop for visual styling of grouped radios (borders between radios)
         */
        this.grouped = false;
        /**
         * Whether the radios are displayed inline (horizontally) or stacked (vertically)
         */
        this.inline = false;
        /**
         * Roving tabindex controller for keyboard navigation
         */
        this.rovingTabindex = new RovingTabindexController(this, {
            getItems: () => this._radios,
            onSelect: (radio, event) => this.selectRadio(radio, event),
            direction: 'both',
            selectOnFocus: true,
        });
        /**
         * Form control integration
         */
        this._formController = new FormControlController(this, {
            value: () => this.value,
            // disabled: () => this.disabled,
            setValue: (control, value) => {
                const radioGroup = control;
                radioGroup.value = value;
            },
        });
        /**
         * Handle slot changes (when radios are added/removed)
         */
        this._handleSlotChange = () => {
            this._syncValueFromRadios();
            this._updateRadiosState();
            // Sync group state to newly added radios
            this._syncGroupStateToRadios();
        };
        this._handleLabelSlotChange = () => {
            const labelId = this.generateId('it-radio-group-label');
            this._label[0]?.setAttribute('id', labelId);
            this.setAttribute('aria-labelledby', labelId);
        };
    }
    /**
     * Override: Custom validity check for radio group
     */
    get validity() {
        // Fallback: create a custom ValidityState-like object
        const valid = !this.required || !!this.value;
        return {
            valid,
            valueMissing: this.required && !this.value,
            typeMismatch: false,
            patternMismatch: false,
            tooLong: false,
            tooShort: false,
            rangeUnderflow: false,
            rangeOverflow: false,
            stepMismatch: false,
            badInput: false,
            customError: false,
        };
    }
    /**
     * Override: Check validity for radio group
     */
    checkValidity() {
        if (this.required && !this.value) {
            return false;
        }
        return true;
    }
    /**
     * Override: Report validity for radio group
     */
    reportValidity() {
        const isValid = this.checkValidity();
        this.handleValidationMessages();
        this._updateInvalidState();
        return isValid;
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        // Only trigger validation state if message is non-empty (actual error)
        // Empty message during init or after correction should not trigger invalid state initially
        this.validationMessage = message;
        this.formControlController.updateValidity();
        // Update aria-invalid on group and children immediately
        this._updateInvalidState();
    }
    handleValidationMessages() {
        const _v = this.validity;
        if (!this.customValidation) {
            if (_v.valueMissing) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else
                this.setCustomValidity('');
        }
    }
    _updateInvalidState() {
        // Only show aria-invalid if validation has been triggered (ARIA best practice)
        const invalid = this.checkValidity() === false || !!this.validationMessage;
        if (invalid)
            this.setAttribute('aria-invalid', 'true');
        else
            this.removeAttribute('aria-invalid');
        this._radios?.forEach((r) => {
            if (invalid)
                r.setAttribute('aria-invalid', 'true');
            else
                r.removeAttribute('aria-invalid');
        });
        // Update validation message in light DOM
        this._updateValidationMessage();
    }
    /**
     * Update validation message in light DOM (for aria-describedby cross-boundary support)
     */
    _updateValidationMessage() {
        const validityMessage = this.validationMessage;
        const messageId = `invalid-feedback-${this._id}`;
        // Find or create validation message element in light DOM
        let messageEl = this.querySelector(`[slot="validation-message"]`);
        if (validityMessage && validityMessage.length > 0) {
            // Create message element if it doesn't exist
            if (!messageEl) {
                messageEl = document.createElement('div');
                messageEl.setAttribute('slot', 'validation-message');
                this.appendChild(messageEl);
            }
            // Update message content and attributes
            messageEl.id = messageId;
            messageEl.className = 'form-feedback text-danger';
            messageEl.setAttribute('role', 'alert');
            messageEl.removeAttribute('hidden');
            const labelText = this._label?.[0]?.textContent || '';
            messageEl.innerHTML = `<span class="visually-hidden">${labelText}: </span>${validityMessage}`;
            // Update aria-describedby on host
            const existingDescribedBy = this._ariaAttributes['aria-describedby'] || '';
            const ariaDescribedBy = existingDescribedBy ? `${existingDescribedBy} ${messageId}` : messageId;
            this.setAttribute('aria-describedby', ariaDescribedBy);
        }
        else {
            // Remove message element if validation passed
            if (messageEl) {
                messageEl.remove();
            }
            // Update aria-describedby (remove message id)
            const existingDescribedBy = this._ariaAttributes['aria-describedby'] || '';
            if (existingDescribedBy) {
                this.setAttribute('aria-describedby', existingDescribedBy);
            }
            else {
                this.removeAttribute('aria-describedby');
            }
        }
    }
    connectedCallback() {
        super.connectedCallback?.();
        this._handleReady();
        this.setAttribute('role', 'radiogroup');
    }
    firstUpdated() {
        const checkedRadio = this.querySelector('it-radio[checked]');
        if (checkedRadio) {
            this.value = checkedRadio.value;
        }
        this.updateRadios();
    }
    async updateRadios() {
        if (!this.hasUpdated) {
            // Initial validation has to happen after the initial render to allow
            // the buttons to be queries from the rendered <slot> element
            await this.updateComplete;
        }
        const radios = this.querySelectorAll('it-radio');
        // eslint-disable-next-line no-return-assign, no-param-reassign
        radios.forEach((r) => {
            // eslint-disable-next-line no-param-reassign
            r.checked = r.value === this.value;
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
    }
    /**
     * Sync group value from checked radio (initialization)
     */
    _syncValueFromRadios() {
        if (!this._radios) {
            return;
        }
        // Find the first checked radio
        const checkedRadio = this._radios.find((r) => r.checked);
        if (checkedRadio) {
            // Sync the group value from the checked radio
            this.value = checkedRadio.value;
        }
    }
    /**
     * PUBLIC API: Called by radio buttons to select themselves
     */
    selectRadio(radio, event) {
        if (radio.disabled || this.disabled) {
            return;
        }
        this.value = radio.value;
        // Uncheck other radios using their public API
        this._radios.forEach((r) => {
            if (r !== radio) {
                // eslint-disable-next-line no-param-reassign
                r.checked = false;
            }
            else {
                // eslint-disable-next-line no-param-reassign
                r.checked = true;
            }
        });
        // If validation was already triggered, update state after selection
        if (this.formControlController.submittedOnce) {
            // For native validation, clear error if now valid
            if (!this.customValidation) {
                this.handleValidationMessages();
                this._updateInvalidState();
            }
            // For custom validation (JustValidate), the external validator will call setCustomValidity
            // but we still update the state based on current validationMessage
            else {
                this._updateInvalidState();
            }
        }
        if (event.type === 'click' || event.type === 'pointerdown') {
            this._handleClick(event);
        }
        else {
            this._handleChange(event);
        }
    }
    /**
     * PUBLIC API: Handle keyboard navigation from radio buttons
     */
    handleRadioKeyDown(radio, event) {
        // Delegate to roving tabindex controller
        const handled = this.rovingTabindex.handleKeydown(radio, event);
        if (handled)
            this._handleFocus(event);
        else
            this._handleBlur(event);
    }
    /**
     * Synchronize radio button states with group state
     */
    _updateRadiosState() {
        if (!this._radios || this._radios.length === 0) {
            return;
        }
        this._radios.forEach((radio) => {
            // Set checked state based on value
            // eslint-disable-next-line no-param-reassign
            radio.checked = radio.value === this.value;
            // Sync disabled state from group to radios
            // Note: We don't override individual radio's disabled state
            // If the group is disabled, all radios should be disabled
            // But individual radios can be disabled independently
            if (this.disabled) {
                // eslint-disable-next-line no-param-reassign
                radio.disabled = true;
            }
        });
        // Update tabindex using roving tabindex controller
        // Priority: checked and enabled > first enabled > 0
        let tabbableIndex = 0;
        // First, try to find a checked radio that is not disabled
        const checkedEnabledIndex = this._radios.findIndex((r) => r.checked && !r.disabled);
        if (checkedEnabledIndex >= 0) {
            tabbableIndex = checkedEnabledIndex;
        }
        else {
            // If no checked enabled radio, find the first enabled radio
            const firstEnabledIndex = this._radios.findIndex((r) => !r.disabled);
            if (firstEnabledIndex >= 0) {
                tabbableIndex = firstEnabledIndex;
            }
            // If all radios are disabled, tabbableIndex stays 0 (but controller will set all to -1)
        }
        this.rovingTabindex.updateTabindices(tabbableIndex);
    }
    /**
     * Sync group state (name, grouped, inline, required) to child radios
     * This replaces the need for requestUpdate() calls
     */
    _syncGroupStateToRadios() {
        if (!this._radios || this._radios.length === 0) {
            return;
        }
        const groupState = {
            name: this.name,
            grouped: this.grouped,
            inline: this.inline,
            required: this.required,
        };
        this._radios.forEach((radio) => {
            radio.syncFromGroup(groupState);
        });
    }
    updated(changed) {
        super.updated(changed);
        // Update radios when value or name changes
        if (changed.has('value')) {
            this._updateRadiosState();
            this.dispatchEvent(new Event('it-change', { bubbles: true }));
            // Re-validate after value change (for native validation) only if validation was already triggered
            if (!this.customValidation && this.formControlController.submittedOnce) {
                this.handleValidationMessages();
                this._updateInvalidState();
            }
        }
        // If validation message changed, update aria-invalid on group and children
        // Skip on initial render: Lit reports all properties as "changed" on first update,
        // which would mark a required-but-empty group invalid before validation is triggered.
        if (changed.has('validationMessage') && this.formControlController.submittedOnce) {
            this._updateInvalidState();
        }
        // If relevant group properties changed, sync to child radios
        const relevant = ['grouped', 'inline', 'name', 'required', 'disabled'];
        const hasChanged = Array.from(changed.keys()).some((k) => relevant.includes(String(k)));
        if (hasChanged && this._radios?.length) {
            this._syncGroupStateToRadios();
        }
    }
    /**
     * Render the component
     */
    render() {
        const groupWrapperClasses = this.composeClass('it-radio-group', 'it-form__control', this.inline && !this.grouped ? 'it-radio-group-inline' : '', this.grouped && !this.inline ? 'it-radio-group-stacked' : '');
        return html `<slot name="label" @slotchange=${this._handleLabelSlotChange}></slot>
      <div class=${groupWrapperClasses}>
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
      <slot name="validation-message"></slot>`;
    }
};
ItRadioGroup.styles = styles;
ItRadioGroup.formAssociated = true;
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "grouped", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadioGroup.prototype, "inline", void 0);
__decorate([
    queryAssignedElements(),
    __metadata("design:type", Array)
], ItRadioGroup.prototype, "_radios", void 0);
__decorate([
    queryAssignedElements({ slot: 'label' }),
    __metadata("design:type", Array)
], ItRadioGroup.prototype, "_label", void 0);
ItRadioGroup = __decorate([
    customElement('it-radio-group')
], ItRadioGroup);

export { ItRadioGroup };
//# sourceMappingURL=it-radio-group.js.map
